"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { isFilled } from "@prismicio/client";
import { motion } from "framer-motion";
import { MotionPrismicNextImage } from "@/components/ui/motion-prismic-next-image";

/**
 * Props for `MissionVisionAndValuesSection`.
 */
export type MissionVisionAndValuesSectionProps =
  SliceComponentProps<Content.MissionVisionAndValuesSectionSlice>;

/**
 * Component for "MissionVisionAndValuesSection" Slices.
 */
const MissionVisionAndValuesSection: FC<MissionVisionAndValuesSectionProps> = ({
  slice,
}) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#F8FCFF] overflow-hidden"
    >
      <Bounded as="div" className="py-[120px]">
        <div className="flex flex-col gap-4 md:items-center justify-center">
          <motion.div
            className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] text-left md:text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <PrismicRichText
              field={slice.primary.title}
              components={{
                label: ({ node, children }) => {
                  if (node.data.label === "Highlight") {
                    return <span className="text-secondary">{children}</span>;
                  }
                  return <span className="text-primary">{children}</span>;
                },
              }}
            />
          </motion.div>
          <motion.p
            className="font-merriweather font-normal text-xl leading-8 md:text-center text-[#033D61] max-w-[394px] mb-8 text-left"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {slice.primary.description}
          </motion.p>
          <div className="border-t border-[#045B9120] max-w-[1107px]">
            {slice.primary.contents.map((content, index) => (
              <div
                key={content.title}
                className="border-b border-[#045B9120] py-12 flex flex-col md:flex-row md:items-center justify-between gap-6"
              >
                <div className="flex items-center gap-6">
                  <MotionPrismicNextImage
                    field={content.icon}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.5 * (index + 1) }}
                  />
                  <motion.div
                    className="text-[#288271] font-archivo font-semibold text-3xl leading-10 min-w-[236px]"
                    initial={{ opacity: 0, x: -100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.5 * (index + 1) }}
                  >
                    {content.title}
                  </motion.div>
                </div>
                <motion.div
                  className="flex-1 flex flex-col xl:flex-row xl:items-center gap-1.5 xl:gap-[137px] text-[#033D61] font-merriweather font-normal text-lg leading-7"
                  initial={{ opacity: 0, x: 100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5 * (index + 1) }}
                >
                  <div>
                    <PrismicRichText
                      field={content.content}
                      components={{
                        list: ({ node, children }) => {
                          return (
                            <span className="list-disc list-inside">
                              {children}
                            </span>
                          );
                        },
                        oList: ({ node, children }) => {
                          return (
                            <span className="list-decimal list-inside">
                              {children}
                            </span>
                          );
                        },
                        label: ({ node, children }) => {
                          if (node.data.label === "Highlight") {
                            return (
                              <span className="font-bold">{children}</span>
                            );
                          }
                          return (
                            <span className="font-normal">{children}</span>
                          );
                        },
                      }}
                    />
                  </div>
                  {isFilled.richText(content.column_2_content) && (
                    <div>
                      <PrismicRichText
                        field={content.column_2_content}
                        components={{
                          list: ({ node, children }) => {
                            return (
                              <span className="list-disc list-inside">
                                {children}
                              </span>
                            );
                          },
                          oList: ({ node, children }) => {
                            return (
                              <span className="list-decimal list-inside">
                                {children}
                              </span>
                            );
                          },
                          label: ({ node, children }) => {
                            if (node.data.label === "Highlight") {
                              return (
                                <span className="font-bold">{children}</span>
                              );
                            }
                            return (
                              <span className="font-normal">{children}</span>
                            );
                          },
                        }}
                      />
                    </div>
                  )}
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </Bounded>
    </section>
  );
};

export default MissionVisionAndValuesSection;
